import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const REASONS = [
  { v: "payment",   l: "Payment problem — mismatch, didn't collect, extra demand" },
  { v: "condition", l: "Parcel condition — damaged, tampered, missing items" },
  { v: "carrier",   l: "Carrier behaviour — rude, no-show, delayed, abusive" },
  { v: "other",     l: "Something else" },
];

const ReportIssueButton = ({ deliveryId, size = "sm", onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error("Please pick the issue type."); return; }
    setBusy(true);
    try {
      const r = REASONS.find((x) => x.v === reason);
      await api.post(`/deliveries/${deliveryId}/disputes`, {
        reason: r?.l || reason,
        details: details.trim(),
      });
      toast.success("Reported — our team will reach out.");
      setOpen(false); setReason(""); setDetails("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit the report.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        data-testid={`report-issue-${deliveryId}`}
        onClick={() => setOpen(true)}
        variant="outline"
        size={size}
        className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
      >
        <Flag className="w-3.5 h-3.5 mr-1"/> Report an issue
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle>Report an issue</DialogTitle>
            <DialogDescription>
              Only you, the other party, and the CarryIt team can see this report. We review every one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">What's happening?</div>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="report-issue-reason"><SelectValue placeholder="Pick one"/></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              data-testid="report-issue-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Add any detail you'd like us to see (optional)"
              rows={4}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit} disabled={busy}
              data-testid="report-issue-submit"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy ? "Submitting…" : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportIssueButton;
